import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/**
 * Link an athlete to their VALD profile. Unlinked athletes are skipped entirely
 * by the sync, so their testing never appears however many times it runs.
 * Accepts a pasted VALD Hub URL or a bare profile id — the URL is what a coach
 * actually has to hand, and it works even when the staged profile list is stale.
 */
export function LinkValdProfile({ athleteId, athleteName }: { athleteId: string; athleteName: string }) {
  const qc = useQueryClient()
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const save = async () => {
    const match = value.match(UUID)
    if (!match) {
      setError('Paste the VALD profile link (or its id) — it should contain a long id like 7f693b46-…')
      return
    }
    setBusy(true)
    setError(null)
    const { error: err } = await supabase
      .from('athletes')
      .update({ vald_profile_id: match[0] })
      .eq('id', athleteId)
    setBusy(false)
    if (err) return setError(err.message)
    setDone(true)
    qc.invalidateQueries({ queryKey: ['athlete', athleteId] })
  }

  if (done) {
    return (
      <div className="rounded-card border border-success/40 bg-success/10 px-4 py-3">
        <p className="font-condensed text-sm font-bold uppercase tracking-[0.12em] text-success">
          ✓ VALD profile linked
        </p>
        <p className="mt-1 text-xs text-muted">
          {athleteName}'s results appear after the next sync — tonight, or run one now.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-card border border-gold/40 bg-gold/[.06] px-4 py-3">
      <p className="font-condensed text-sm font-bold uppercase tracking-[0.12em] text-gold">
        ⚠ Not linked to VALD
      </p>
      <p className="mt-1 text-xs text-muted">
        {athleteName} isn't connected to a VALD profile, so no test results can sync. Open their
        profile in VALD Hub and paste the page link below.
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://hub.valdperformance.com/app/profiles/…"
          className="min-w-[220px] flex-1"
        />
        <Button size="md" disabled={busy || !value.trim()} onClick={save}>
          {busy ? 'Linking…' : 'Link profile'}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  )
}
